import React from "react";

import { useNavigate } from "react-router-dom";

import { RESELLER_SUBSCRIPTION_TICKET_URL } from "../utils/ticketIntents";

/**
 * Subscription packet pricing chart (Packet | Price | $/Key). Values come
 * from the reseller pricing sheet; do not edit without updated pricing.
 */
const PACKET_PRICING = [
  { packet: "500", price: "$899", perKey: 1.8 },
  { packet: "1000", price: "$1,699", perKey: 1.7 },
  { packet: "1500", price: "$2,430", perKey: 1.62 },
  { packet: "2000", price: "$3,120", perKey: 1.56 },
  { packet: "2500", price: "$3,775", perKey: 1.51 },
  { packet: "3000", price: "$4,380", perKey: 1.46 },
  { packet: "3500", price: "$4,970", perKey: 1.42 },
  { packet: "4000", price: "$5,560", perKey: 1.39 },
  { packet: "4500", price: "$6,120", perKey: 1.36 },
  { packet: "5000", price: "$6,700", perKey: 1.34 },
  { packet: "5500", price: "$7,260", perKey: 1.32 },
  { packet: "6000", price: "$7,800", perKey: 1.3 },
  { packet: "6500", price: "$8,320", perKey: 1.28 },
  { packet: "7000", price: "$8,820", perKey: 1.26 },
  { packet: "7500", price: "$9,300", perKey: 1.24 },
  { packet: "8000", price: "$9,760", perKey: 1.22 },
  { packet: "8500", price: "$10,200", perKey: 1.2 },
  { packet: "9000", price: "$10,620", perKey: 1.18 },
  { packet: "9500", price: "$11,020", perKey: 1.16 },
  { packet: "10,000", price: "$11,400", perKey: 1.14 },
];

const MIN_PER_KEY = Math.min(...PACKET_PRICING.map((row) => row.perKey));
const MAX_PER_KEY = Math.max(...PACKET_PRICING.map((row) => row.perKey));
const ENTRY_PACKET = PACKET_PRICING[0];
const BEST_PACKET = PACKET_PRICING[PACKET_PRICING.length - 1];
const SUGGESTED_PACKET =
  PACKET_PRICING.find((row) => row.packet === "5000") ?? PACKET_PRICING[9];
const SUGGESTED_SAVINGS_PERCENT = Math.round(
  ((ENTRY_PACKET.perKey - SUGGESTED_PACKET.perKey) / ENTRY_PACKET.perKey) * 100
);
const PACKET_PRICING_COLUMNS = [
  {
    label: "Starter packets",
    range: "500 – 5,500",
    rows: PACKET_PRICING.slice(0, 10),
  },
  {
    label: "Scale packets",
    range: "6,000 – 10,000",
    rows: PACKET_PRICING.slice(10),
  },
];

function getSavingsBarWidth(perKey) {
  const range = MAX_PER_KEY - MIN_PER_KEY;

  if (range <= 0) {
    return 100;
  }

  return ((MAX_PER_KEY - perKey) / range) * 100;
}

function PacketPricingHeader() {
  return (
    <div
      aria-hidden="true"
      className="grid grid-cols-[minmax(0,1fr)_5.5rem_6.5rem] sm:grid-cols-[minmax(0,1fr)_6rem_7.5rem] gap-3 px-4 sm:px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40"
    >
      <span>Packet</span>
      <span className="text-right">Price</span>
      <span className="text-right">$/Key</span>
    </div>
  );
}

function PacketPricingRow({ highlight, row }) {
  const savingsWidth = getSavingsBarWidth(row.perKey);

  return (
    <div
      className={`flex-1 grid grid-cols-[minmax(0,1fr)_5.5rem_6.5rem] sm:grid-cols-[minmax(0,1fr)_6rem_7.5rem] gap-3 items-center px-4 sm:px-5 py-2 border-b border-gray-100 dark:border-gray-700/60 last:border-b-0 transition-colors hover:bg-gray-50/80 dark:hover:bg-gray-900/30 ${
        highlight === "best"
          ? "bg-primary-50/60 dark:bg-primary-900/10"
          : highlight === "suggested"
            ? "bg-primary-50/80 dark:bg-primary-900/15 ring-1 ring-inset ring-primary-200/60 dark:ring-primary-800/40"
            : highlight === "entry"
              ? "bg-gray-50/50 dark:bg-gray-900/20"
              : ""
      }`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
          {row.packet}
        </span>
        {highlight === "entry" && (
          <span className="inline-flex items-center rounded-full bg-gray-200/80 dark:bg-gray-700/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-300">
            Entry
          </span>
        )}
        {highlight === "suggested" && (
          <span className="inline-flex items-center rounded-full bg-primary-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-300">
            Suggested
          </span>
        )}
        {highlight === "best" && (
          <span className="inline-flex items-center rounded-full bg-primary-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-300">
            Best
          </span>
        )}
      </div>
      <span className="text-right text-sm text-gray-700 dark:text-gray-300 tabular-nums">
        {row.price}
      </span>
      <div className="flex items-center justify-end gap-2 min-w-0">
        <span
          aria-hidden="true"
          className="hidden sm:block h-1.5 w-10 rounded-full bg-gray-100 dark:bg-gray-900/60 overflow-hidden flex-shrink-0"
          title={`${Math.round(savingsWidth)}% savings vs entry rate`}
        >
          <span
            className="block h-full rounded-full bg-gradient-to-r from-primary-400 to-primary-500"
            style={{ width: `${savingsWidth}%` }}
          />
        </span>
        <span className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
          ${row.perKey.toFixed(2)}
        </span>
      </div>
    </div>
  );
}

function PacketPricingColumn({ label, range, rows }) {
  return (
    <div className="min-w-0 h-full flex flex-col rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/40 dark:bg-gray-900/20 overflow-hidden shadow-sm">
      <div className="px-4 sm:px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0">
        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-primary-600 dark:text-primary-300">
          {label}
        </p>
        <p className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
          {range}
          <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
            {" "}
            codes
          </span>
        </p>
      </div>
      <PacketPricingHeader />
      <div className="flex-1 flex flex-col">
        {rows.map((row) => (
          <PacketPricingRow
            highlight={
              row.packet === ENTRY_PACKET.packet
                ? "entry"
                : row.packet === SUGGESTED_PACKET.packet
                  ? "suggested"
                  : row.packet === BEST_PACKET.packet
                    ? "best"
                    : null
            }
            key={row.packet}
            row={row}
          />
        ))}
      </div>
    </div>
  );
}

function PacketPricingRecommendation({ onContact }) {
  const recommendationPoints = [
    {
      icon: "fa-balance-scale",
      text: `Strong per-key value at $${SUGGESTED_PACKET.perKey.toFixed(2)} — about ${SUGGESTED_SAVINGS_PERCENT}% lower than the entry tier without jumping to enterprise volume.`,
    },
    {
      icon: "fa-sync-alt",
      text: "5,000 reusable codes give room to grow while codes return to your pool when keys are revoked or trashed.",
    },
    {
      icon: "fa-plug",
      text: "Large enough for full WHMCS automation — less manual purchasing and fewer renewal surprises.",
    },
    {
      icon: "fa-arrow-up",
      text: "Start here and upgrade to a larger packet anytime as your active user base scales.",
    },
  ];

  return (
    <div className="min-w-0 h-full rounded-xl border border-primary-600/80 dark:border-primary-500/50 bg-gradient-to-br from-primary-600 to-primary-700 text-white overflow-hidden shadow-lg shadow-primary-900/20 flex flex-col">
      <div className="px-5 py-4 border-b border-white/15">
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-flex items-center rounded-full bg-white/15 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em]">
            Our suggestion
          </span>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0 ring-1 ring-white/20">
            <i className="fas fa-star text-sm" />
          </div>
          <div className="min-w-0">
            <p className="text-2xl font-semibold tracking-tight tabular-nums leading-none">
              {SUGGESTED_PACKET.packet}
            </p>
            <p className="mt-1 text-sm text-primary-100">code monthly packet</p>
          </div>
        </div>
      </div>

      <div className="px-5 py-4 border-b border-white/15 grid grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-primary-200">
            Monthly
          </p>
          <p className="text-lg font-semibold tabular-nums">
            {SUGGESTED_PACKET.price}
          </p>
        </div>
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-primary-200">
            Per key
          </p>
          <p className="text-lg font-semibold tabular-nums">
            ${SUGGESTED_PACKET.perKey.toFixed(2)}
          </p>
        </div>
      </div>

      <div className="px-5 py-4 flex-1">
        <p className="text-sm font-semibold text-white mb-3">
          Why most resellers pick this tier
        </p>
        <ul className="space-y-3">
          {recommendationPoints.map((point) => (
            <li
              className="flex gap-2.5 text-sm text-primary-50 leading-relaxed"
              key={point.icon}
            >
              <i
                className={`fas ${point.icon} text-xs mt-1 flex-shrink-0 text-primary-200`}
              />
              <span>{point.text}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="px-5 py-4 border-t border-white/15 bg-black/10">
        <button
          className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-white text-primary-700 hover:bg-primary-50 px-4 py-3 text-sm font-semibold transition-colors"
          type="button"
          onClick={onContact}
        >
          <i className="fas fa-headset text-xs" />
          Get started with 5,000 codes
        </button>
        <p className="mt-2 text-center text-[11px] text-primary-200/90">
          Opens a support ticket to discuss your subscription upgrade.
        </p>
      </div>
    </div>
  );
}

export function canViewResellerPlans(user) {
  return user?.type === "Reseller";
}

const PLANS = [
  {
    key: "payg",
    name: "Pay-As-You-Go Codes",
    tagline: "Current Reseller Program",
    icon: "fas fa-hand-holding-usd",
    bestFor: "Small or growing resellers - typically under ~500 active users",
    summary: "Flexibility and low entry cost.",
    howItWorks: [
      "Purchase single-use redeem codes individually or in small batches.",
      "Each redeem code is valid for 30 days once claimed.",
      "With Auto-Renew enabled, a claimed LunaKey™ renews automatically as long as unclaimed redeem codes remain available.",
      "Tiered pricing - the more active users (claimed codes) you have, the better the discount on future code purchases.",
    ],
    pros: [
      "Pay only for what you need",
      "Flexible and ideal for early-stage growth",
      "Lower upfront cost",
      "Simple entry point for new resellers",
    ],
    cons: [
      "Manual management required (purchasing and applying codes)",
      "If a user stops paying after a code is claimed, that code is consumed and cannot be reused",
      "Harder to automate at scale",
      "Requires closer monitoring of renewals and inventory",
    ],
  },
  {
    key: "subscription",
    name: "Subscription Program",
    tagline: "Bulk Capacity Model",
    icon: "fas fa-rocket",
    bestFor:
      "Medium to large resellers - typically 500+ active users seeking automation and predictable scaling",
    summary: "Automation, efficiency, and scale.",
    howItWorks: [
      "Purchase a monthly subscription package that includes a fixed pool of redeem codes (e.g., 500 / 1000 / 2000 / 3000).",
      "Redeem codes do not expire while the subscription remains active.",
      "Bulk discounts increase with larger packages.",
      "Fully compatible with WHMCS automation - minimal manual intervention.",
      "Redeem codes are reusable: if a key is revoked or trashed, the code automatically returns to the available pool.",
      "Upgrade to a larger package at any time.",
    ],
    pros: [
      "No code expiration concerns",
      "Reusable redeem codes reduce waste",
      "Full automation with WHMCS - little to no manual work",
      "Direct-to-customer in-app messaging (on v2)",
      "Mass messages to all accounts or to a direct individual account",
      "Instant availability of capacity (no need to repurchase codes frequently)",
    ],
    cons: [
      "No rollover of unused capacity - each month resets to the package limit",
      "Fixed monthly cost, even if not all codes are used",
      "Higher upfront commitment compared to pay-as-you-go",
    ],
  },
];

/**
 * Reseller Plans - side-by-side comparison of the Pay-As-You-Go and
 * Subscription reseller programs plus the subscription packet pricing chart.
 * Visible to both reseller types from the sidebar; static content only.
 */
const ResellerPlans = () => {
  const navigate = useNavigate();

  return (
    <div className="p-4 sm:p-6">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-2xl mb-4 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(600px 220px at 85% -20%, rgba(139, 92, 246, 0.22), transparent 60%), radial-gradient(500px 200px at 10% 120%, rgba(34, 211, 238, 0.12), transparent 60%)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.6), transparent)",
          }}
        />

        <div className="relative p-5 sm:p-7 flex items-start gap-3 sm:gap-4">
          <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary-500/30 ring-1 ring-white/10">
            <i className="fas fa-box-open text-white text-base sm:text-lg" />
          </div>
          <div className="min-w-0">
            <span className="font-mono text-[10px] font-semibold tracking-[0.16em] uppercase text-primary-600 dark:text-primary-300">
              Reseller Programs
            </span>
            <h1 className="text-xl sm:text-[26px] font-semibold text-gray-900 dark:text-white tracking-tight leading-tight">
              Reseller Plans
            </h1>
            <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400 max-w-xl leading-relaxed">
              Two ways to resell Lunova: buy codes as you go, or run on a
              monthly subscription with a reusable code pool and full WHMCS
              automation.
            </p>
          </div>
        </div>
      </div>

      {/* Contact CTA */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white tracking-tight">
            Interested in the subscription program?
          </h2>
          <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
            Contact us or create a support ticket discussing your interest in
            upgrading your experience.
          </p>
        </div>
        <button
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 hover:shadow-xl hover:shadow-primary-500/40 hover:-translate-y-0.5 text-white px-5 py-3 text-sm font-semibold transition-all ring-1 ring-white/10 flex-shrink-0"
          type="button"
          onClick={() => navigate(RESELLER_SUBSCRIPTION_TICKET_URL)}
        >
          <i className="fas fa-headset text-xs" />
          Create Support Ticket
        </button>
      </div>

      {/* Program comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {PLANS.map((plan) => (
          <div
            className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col"
            key={plan.key}
          >
            <div className="p-5 sm:p-6 border-b border-gray-100 dark:border-gray-700/60">
              <div className="flex items-start gap-3">
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ring-1 ${
                    plan.key === "subscription"
                      ? "bg-gradient-to-br from-primary-500 to-primary-700 ring-white/10 shadow-lg shadow-primary-500/30"
                      : "bg-gray-100 dark:bg-gray-900/50 ring-gray-200 dark:ring-gray-700"
                  }`}
                >
                  <i
                    className={`${plan.icon} text-sm ${
                      plan.key === "subscription"
                        ? "text-white"
                        : "text-gray-500 dark:text-gray-400"
                    }`}
                  />
                </div>
                <div className="min-w-0">
                  <span className="font-mono text-[10px] font-semibold tracking-[0.16em] uppercase text-gray-400 dark:text-gray-500">
                    {plan.tagline}
                  </span>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white tracking-tight">
                    {plan.name}
                  </h2>
                </div>
              </div>
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                <i className="fas fa-user-check mr-1.5 text-[10px]" />
                Best for: {plan.bestFor}
              </p>
            </div>

            <div className="p-5 sm:p-6 space-y-5 flex-1">
              <div>
                <h3 className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-[0.1em] mb-2">
                  How it works
                </h3>
                <ul className="space-y-1.5">
                  {plan.howItWorks.map((item) => (
                    <li
                      className="flex gap-2 text-sm text-gray-700 dark:text-gray-300 leading-relaxed"
                      key={item}
                    >
                      <i className="fas fa-circle text-[4px] mt-2 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-[0.1em] mb-2">
                  Pros
                </h3>
                <ul className="space-y-1.5">
                  {plan.pros.map((item) => (
                    <li
                      className="flex gap-2 text-sm text-gray-700 dark:text-gray-300 leading-relaxed"
                      key={item}
                    >
                      <i className="fas fa-check-circle text-emerald-500 text-xs mt-1 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-[0.1em] mb-2">
                  Cons
                </h3>
                <ul className="space-y-1.5">
                  {plan.cons.map((item) => (
                    <li
                      className="flex gap-2 text-sm text-gray-700 dark:text-gray-300 leading-relaxed"
                      key={item}
                    >
                      <i className="fas fa-minus-circle text-amber-500 text-xs mt-1 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="px-5 sm:px-6 py-4 border-t border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-900/30">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                <span className="font-semibold text-gray-900 dark:text-white">
                  In short:
                </span>{" "}
                {plan.summary}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Subscription packet pricing */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden mb-4">
        <div className="p-5 sm:p-6 border-b border-gray-100 dark:border-gray-700/60">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <div className="min-w-0">
              <span className="font-mono text-[10px] font-semibold tracking-[0.16em] uppercase text-primary-600 dark:text-primary-300">
                Subscription Program
              </span>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white tracking-tight">
                Packet Pricing
              </h2>
              <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400 max-w-xl leading-relaxed">
                Monthly packages include a fixed pool of reusable redeem codes.
                The per-key rate drops as the packet grows.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/40 px-3 py-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Entry
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
                  ${ENTRY_PACKET.perKey.toFixed(2)}
                  <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                    {" "}
                    / key
                  </span>
                </p>
              </div>
              <div className="rounded-xl border border-primary-200 dark:border-primary-800/60 bg-primary-50/70 dark:bg-primary-900/20 px-3 py-2">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-primary-600 dark:text-primary-300">
                  Best rate
                </p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">
                  ${BEST_PACKET.perKey.toFixed(2)}
                  <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                    {" "}
                    / key
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-4 sm:p-5 items-stretch">
          {PACKET_PRICING_COLUMNS.map((column) => (
            <PacketPricingColumn
              key={column.range}
              label={column.label}
              range={column.range}
              rows={column.rows}
            />
          ))}
          <PacketPricingRecommendation
            onContact={() => navigate(RESELLER_SUBSCRIPTION_TICKET_URL)}
          />
        </div>
        <div className="px-5 sm:px-6 py-3 border-t border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-900/30">
          <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
            <i className="fas fa-info-circle mr-1.5 text-[10px]" />
            Prices are monthly. Purple bars show savings vs the entry rate —
            fuller bars mean a lower per-key cost. Upgrade to a larger packet
            anytime.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResellerPlans;
